/**
 * Direct-to-DeepSeek mode ("own backend").
 *
 * When `DEEPSEEK_API_KEY` is set, the SDK routes model calls straight to
 * DeepSeek's OpenAI-compatible API instead of the Codebuff backend, and the
 * CLI bypasses backend auth/session gates with a local identity. When unset,
 * every path behaves exactly as before (Codebuff backend).
 *
 * `DEEPSEEK_MODEL` optionally overrides the model ID sent to DeepSeek. Without
 * it, DeepSeek's API serves exactly two model names — `deepseek-v4-pro` and
 * `deepseek-v4-flash` (a request for anything else is rejected with "The
 * supported API model names are deepseek-v4-pro or deepseek-v4-flash"). So:
 *
 *  - `deepseek/…` lane ids keep their model name (`deepseek/deepseek-v4-pro`
 *    → `deepseek-v4-pro`), with the `-max` context tiers normalized to their
 *    base model and the legacy fireworks lane mapped to flash.
 *  - Every other provider id (anthropic/*, openai/*, google/*, …) cannot be
 *    served by DeepSeek at all and falls back to the default DeepSeek model.
 */

export const DEEPSEEK_BASE_URL = 'https://api.deepseek.com'

/** Model name sent to DeepSeek for ids that are not a DeepSeek lane model. */
export const DEFAULT_DIRECT_DEEPSEEK_MODEL = 'deepseek-v4-pro'

/** The user's DeepSeek API key, or undefined when direct mode is off. */
export const getDirectDeepSeekApiKey = (): string | undefined =>
  // Dot access (not bracket) so `bun build --define process.env.DEEPSEEK_API_KEY=…`
  // and `--env=inline` can bake the value into compiled binaries — bun does not
  // replace or inline dynamic `process.env[name]` reads.
  process.env.DEEPSEEK_API_KEY

/** True when the CLI/SDK should talk to DeepSeek directly instead of Codebuff. */
export const isDirectDeepSeekMode = (): boolean =>
  Boolean(getDirectDeepSeekApiKey())

/** Map a Codebuff model ID to the model name DeepSeek's API accepts. */
export function remapModelForDirectDeepSeek(model: string): string {
  const override = process.env.DEEPSEEK_MODEL
  if (override) return override

  // DeepSeek lane ids: keep the model name, normalizing the -max context tiers
  // and the legacy fireworks lane to ids the API actually serves.
  if (model.startsWith('deepseek/')) {
    const name = model.slice('deepseek/'.length)
    if (name === 'deepseek-v4-pro' || name === 'deepseek-v4-flash') {
      return name
    }
    if (name === 'deepseek-v4-pro-max') return 'deepseek-v4-pro'
    if (name === 'deepseek-v4-flash-max') return 'deepseek-v4-flash'
    return DEFAULT_DIRECT_DEEPSEEK_MODEL
  }
  if (model === 'fireworks/deepseek-v4-flash') return 'deepseek-v4-flash'

  // Any other provider (anthropic, openai, google, …) has no DeepSeek
  // equivalent — fall back so the request is never rejected for the model name.
  return DEFAULT_DIRECT_DEEPSEEK_MODEL
}
